import type { fabric } from 'fabric';
import type { ProjectPage } from '@canvas/shared-types';
import { renderPageToDataUrl } from '@/lib/renderPage';

export const OPEN_PRESENT_EVENT = 'craftara:open-present';

export function requestOpenPresent(): void {
  window.dispatchEvent(new CustomEvent(OPEN_PRESENT_EVENT));
}

/**
 * Capture the live artboard as a single presentable slide image.
 */
export function capturePresentSlides(canvas: fabric.Canvas): string[] {
  const prevActive = canvas.getActiveObject();
  canvas.discardActiveObject();
  canvas.requestRenderAll();

  const multiplier = Math.min(2, typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
  const dataUrl = canvas.toDataURL({
    format: 'png',
    multiplier,
    enableRetinaScaling: true,
  });

  if (prevActive) {
    canvas.setActiveObject(prevActive);
    canvas.requestRenderAll();
  }

  return [dataUrl];
}

/**
 * Capture every project page for Present mode.
 * Active page uses the live Fabric canvas; others render offscreen.
 */
export async function captureAllPresentSlides(options: {
  canvas: fabric.Canvas;
  pages: ProjectPage[];
  activePageId: string;
}): Promise<string[]> {
  const { canvas, pages, activePageId } = options;
  if (!pages.length) return capturePresentSlides(canvas);

  const multiplier = Math.min(2, typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
  const live = capturePresentSlides(canvas)[0];
  const slides: string[] = [];

  for (const page of pages) {
    if (page.pageId === activePageId && live) {
      slides.push(live);
      continue;
    }
    try {
      slides.push(await renderPageToDataUrl(page.canvasData, multiplier));
    } catch (err) {
      console.warn('Failed to render page for present', page.pageId, err);
      slides.push(live || '');
    }
  }

  return slides.filter(Boolean);
}
